import useTheme from '@mui/material/styles/useTheme';
import { CXAvatar } from 'common/components/cx-avatar/cx-avatar';
import Box from 'common/components/material/Box';
import { DrawerHeader } from 'common/components/material/Drawer';
import Skeleton from 'common/components/material/Skeleton';
import Stack from 'common/components/material/Stack';
import Typography from 'common/components/material/Typography';
import { ButtonTabs, CloseButton } from 'common/components/navigation';
import { ExploreTabEnum } from 'common/enum/ExploreTab.enum';
import useDrawer from 'common/hooks/useDrawer';
import useQuery from 'common/hooks/useQuery';
import useSession from 'common/hooks/useSession';
import useIntersectionObserver from 'features/org-root/hooks/useIntersectionObserver';
import EndorsedSkillTrackRecord from 'features/profiles/components/EdnorsedSkillTrackRecord';
import { SearchFilters } from 'features/search/types/filters.type';
import { SearchParams } from 'features/search/types/search-params.type';
import React, { useState } from 'react';
import {
  CUT_OFF_DAY_COUNT,
  DrawerTitle,
  faintScrollbarStyles,
  MATCHING_ENGINE_PERSON_DRAWER_ID,
  matchingEngineApi,
  MatchingEngineContributorDetailDrawer,
} from '../matching-engine.utils';
import { MatchingEnginePersonDrawerSummary } from './matching-engine-person-drawer-summary';
import { ProfileSkillBreakdown } from './MatchingEngineComponents/ProfileSkillBreakdown';
import { SkillGroups } from './MatchingEngineComponents/ProfileSkillBreakdown';
import { UseQueryOptions } from 'common/types/Api.type';
import { FeedbackOverview } from './FeedbackOverview';
import { Tooltip } from 'common/components/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import colorPalette from 'common/theme/colorPalette';
import { BaseWorkItem, SkillContributionsContainer } from './ManageSkillDashboardDrawer';
import { useGetWorkItems } from '../hooks/useGetWorkItems';

const MATCHING_ENGINE_CONTRIBUTOR_DETAILS_DRAWER_TABS = [
  {
    value: MatchingEngineContributorDetailDrawer.LastAllocation,
    label: `Recent Activity (Last ${CUT_OFF_DAY_COUNT}d)`,
  },
  {
    value: MatchingEngineContributorDetailDrawer.TrackRecord,
    label: 'Track Record',
  },
  {
    value: MatchingEngineContributorDetailDrawer.Feedback,
    label: 'Feedback',
  },
  {
    value: MatchingEngineContributorDetailDrawer.Skills,
    label: 'Skills',
  },
];

export type GetMatchingEngineSkillsRequestParams = SearchParams &
  SearchFilters & {
    contentType: ExploreTabEnum;
    cursor: string;
    size: number;
  };

export const getMatchingEngineSkills = (params: GetMatchingEngineSkillsRequestParams) => {
  return matchingEngineApi.get('skills', { params });
};

export const getPerformanceRanks = (params: SearchParams & SearchFilters) => {
  return matchingEngineApi.get('performance-ranks', { params });
};

export const useGetPerformanceReviews = (profileId?: string, options?: UseQueryOptions) => {
  const { account: user } = useSession();

  const getPerformanceReviewsApi = () => {
    const params = {
      profileId,
    };
    return matchingEngineApi.get(`profile-performance-review`, { params });
  };

  const queryKey = [
    'matching-engine',
    user?.organizationId,
    profileId,
    'profile-performance-review',
  ];
  const queryResponse = useQuery(queryKey, getPerformanceReviewsApi, {
    enabled: true,
    ...options,
  });

  return {
    ...queryResponse,
    data: queryResponse.data?.data?.result || [],
  };
};

export const getGroupedWorkItems = (data: BaseWorkItem[]) => {
  const groups = new Map<string, { groupHeader: BaseWorkItem; items: BaseWorkItem[] }>();

  (data ?? []).forEach((item) => {
    const key = item.id ?? item.name;
    const existing = groups.get(key);

    if (existing) {
      existing.items.push(item);

      if (item.criticality === 'High' && existing.groupHeader.criticality !== 'High') {
        existing.groupHeader = {
          ...existing.groupHeader,
          criticality: 'High',
        };
      }
    } else {
      groups.set(key, {
        groupHeader: item,
        items: [item],
      });
    }
  });

  return Array.from(groups.values());
};

const RecentAllocations: React.FC<{
  profileId: string;
}> = ({ profileId }) => {
  const { palette } = useTheme();
  const { setNode } = useIntersectionObserver();
  const cutOffDays = 30;
  const { data, isLoading } = useGetWorkItems({ profileId, cutOffDays });
  const groupedItems = React.useMemo(() => getGroupedWorkItems(data), [data]);
  if (isLoading || !data) {
    return (
      <Skeleton
        ref={setNode}
        variant="rectangular"
        sx={{ borderRadius: '0.75rem', mr: 2, mb: 3 }}
        height="6rem"
      />
    );
  }

  if (groupedItems?.length === 0) {
    return (
      <Typography variant="body4" color={palette.Text.Headline} sx={{ fontStyle: 'italic' }}>
        Allocation details not available.
      </Typography>
    );
  }

  return (
    <Box>
      {groupedItems.map(({ groupHeader, items }) => (
        <Box key={groupHeader.id} sx={{ mb: 3 }}>
          <Stack spacing={1} px={2}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography component="span" fontSize="0.8rem" fontWeight={600}>
                {groupHeader.name}
              </Typography>
              {groupHeader.criticality === 'High' && (
                <WarningIcon sx={{ color: colorPalette.YELLOW400, fontSize: 16 }} />
              )}
            </Stack>

            {items.map((item, idx) => (
              <SkillContributionsContainer
                key={`${item.id}-${idx}`}
                cutOffDays={cutOffDays}
                profileId={profileId}
                workItem={item}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};

// PersonLinkContainer rip-off -- main differences:
// - allow profileHeadline's tooltip to be different from profileHeadline (this way we can
//   inject more information in the tooltip)
// - (in future) on click, we should open a side drawer, rather than navigating to someone's
//   profile.

export const MatchingEnginePersonDrawer: React.FC<{
  person: any;
  initialTab: MatchingEngineContributorDetailDrawer;
  skillGroups: SkillGroups;
  selectedSkillId?: string;
  shouldShowSkillTab: boolean;
  selectedSkills?: any[];
}> = ({
  person,
  initialTab,
  skillGroups,
  selectedSkillId,
  shouldShowSkillTab = false,
  selectedSkills,
}) => {
  const theme = useTheme();
  const { closeDrawer } = useDrawer();
  const fullName = person?.name;
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const visibleTabs = shouldShowSkillTab
    ? MATCHING_ENGINE_CONTRIBUTOR_DETAILS_DRAWER_TABS.filter(
        (tab) => tab.value !== MatchingEngineContributorDetailDrawer.TrackRecord,
      )
    : MATCHING_ENGINE_CONTRIBUTOR_DETAILS_DRAWER_TABS.filter(
        (tab) => tab.value !== MatchingEngineContributorDetailDrawer.Skills,
      );

  const closeCurrentDrawer = () => {
    closeDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID);
  };

  return (
    <>
      <DrawerHeader>
        <Stack flexDirection="column" pl={2} pt={2} width="100%">
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
            <Stack flexDirection="row" alignItems="center">
              <CXAvatar src={person?.photo} alt={person?.name} />
              <DrawerTitle ml={2}>{fullName}</DrawerTitle>
            </Stack>

            <CloseButton clickHandler={closeCurrentDrawer} />
          </Stack>
        </Stack>
      </DrawerHeader>

      <Box
        sx={{
          ...faintScrollbarStyles(theme),
          height: 'calc(100% - 72px)',
          overflowY: 'auto',
          px: 3,
        }}
      >
        <MatchingEnginePersonDrawerSummary person={person} skillGroups={skillGroups} />

        <ButtonTabs
          selectedValues={[selectedTab]}
          tabs={visibleTabs}
          onChange={setSelectedTab}
          scroll
          sx={{
            px: { xs: 1.5, md: 0 },
            py: 2,
            gap: 0.75,
          }}
        />
        {(() => {
          switch (selectedTab) {
            case MatchingEngineContributorDetailDrawer.LastAllocation:
              return <RecentAllocations profileId={person.profileID} />;
            case MatchingEngineContributorDetailDrawer.TrackRecord:
              return (
                <EndorsedSkillTrackRecord
                  profileId={person.profileID}
                  skillId={person.skillID}
                />
              );
            case MatchingEngineContributorDetailDrawer.Feedback:
              return <FeedbackOverview profileId={person.profileID} />;
            case MatchingEngineContributorDetailDrawer.Skills:
              return (
                <ProfileSkillBreakdown
                  profileId={person.profileID}
                  skillGroups={skillGroups}
                  selectedSkillId={selectedSkillId}
                />
              );
            default:
              return null;
          }
        })()}
      </Box>
    </>
  );
};
